import axios from 'axios';
import { francAll } from 'franc';
import { PACOCA_API_URL } from './vars';

// Cache simples em memória para detecção e tradução
const langCache = new Map<string, string>();
const translateCache = new Map<string, string>();

// ─── Helpers de idioma do usuário ────────────────────────────────────────────

export const userLang = (): string => {
    const map: Record<string, string> = { pt: 'por', es: 'spa', us: 'eng' };
    return map[localStorage.getItem("language") ?? ''] ?? 'por';
};

const userLangToTranslate = (): string => {
    const lang = localStorage.getItem("language");
    if (!lang) return 'pt';
    return lang === 'us' ? 'en' : lang;
};

export const isUserLang = (lang: string) => lang === userLang();

export const langToListen = (textLang: string): string => {
    const map: Record<string, string> = {
        por: 'pt-BR', eng: 'en-US', en: 'en-US',
        spa: 'es-ES', es: 'es-ES', und: 'pt-BR',
    };
    return map[textLang] ?? 'pt-BR';
};

// ─── Normalização ─────────────────────────────────────────────────────────────

export const normalizeTextForLangDetect = (text: string): string =>
    text
        .replace(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F/gu, '')
        .replace(/:[a-zA-Z0-9_]+:/g, '')
        .replace(/<[^>]*>/g, '')
        .replace(/https?:\/\/\S+/g, '')           // remove URLs (confundem franc)
        .replace(/@\w+/g, '')                      // remove @menções
        .replace(/#\w+/g, '')                      // remove #hashtags
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')         // mantém só letras, números, espaços
        .replace(/\s+/g, ' ')
        .trim();

// ─── Chave de cache (primeiros 80 chars normalizados) ────────────────────────

const cacheKey = (text: string) =>
    normalizeTextForLangDetect(text).slice(0, 80).toLowerCase();

// ─── Detecção de idioma ───────────────────────────────────────────────────────

export const detectLanguage = (text?: string): string => {
    if (!text) return userLang();

    const normalized = normalizeTextForLangDetect(text);

    // Texto muito curto → não vale a pena detectar
    if (normalized.length < 15) return userLang();

    const key = cacheKey(text);
    if (langCache.has(key)) return langCache.get(key)!;

    const results = francAll(normalized, { only: ['por', 'spa', 'eng'] });

    // Exige confiança mínima e diferença clara entre 1º e 2º lugar
    const [[lang1, score1], [, score2] = ['', 0]] = results;
    const confident = score1 > 0.5 && (score1 - score2) > 0.15;

    const detected = confident ? lang1 : userLang();

    langCache.set(key, detected);

    // Evita cache infinito
    if (langCache.size > 200) {
        const firstKey = langCache.keys().next().value;
        if(firstKey)
            langCache.delete(firstKey);
    }

    return detected;
};

// ─── Tradução com cache e early-return ───────────────────────────────────────

export const translate = async (text?: string): Promise<string | null> => {
    if (!text?.trim()) return "";

    const normalized = normalizeTextForLangDetect(text);
    if (normalized.length < 5) return "";

    const detected = detectLanguage(text);
    if (isUserLang(detected)) return text;

    const key = `${cacheKey(text)}__${userLangToTranslate()}`;
    if (translateCache.has(key)) return translateCache.get(key)!;

    try {
        // Remove emojis customizados antes de enviar à API,
        // e restaura depois com o texto original
        const { clean, tokens } = extractCustomEmojis(text);

        const res = await axios.post(`${PACOCA_API_URL}/translate`, {
            text: clean,
            target: userLangToTranslate(),
        }, {
            headers: { "Content-Type": "application/json" }
        });

        let translated: string = res.data.translatedText;
        if (!translated) return "";

        // Restaura os emojis customizados nas posições originais
        translated = restoreCustomEmojis(translated, tokens);

        translateCache.set(key, translated);

        if (translateCache.size > 100) {
            const firstKey = translateCache.keys().next().value;
            if (firstKey) translateCache.delete(firstKey);
        }

        return translated || "";
    } catch (err: any) {
        console.error("Translation error:", err?.response?.data ?? err);
        return "";
    }
};

// ─── Helpers de emoji customizado ────────────────────────────────────────────

// Captura qualquer :palavra: e tags <img class="emoji">
const CUSTOM_EMOJI_REGEX = /:[a-zA-Z0-9_]+:|<img[^>]+class=['"]emoji['"][^>]*>/g;

function extractCustomEmojis(text: string): { clean: string; tokens: Record<string, string> } {
    const tokens: Record<string, string> = {};
    let i = 0;

    const clean = text.replace(CUSTOM_EMOJI_REGEX, (match) => {
        const placeholder = `__EMJ${i++}__`;
        tokens[placeholder] = match;
        return placeholder;
    });

    return { clean, tokens };
}

function restoreCustomEmojis(text: string, tokens: Record<string, string>): string {
    return Object.entries(tokens).reduce(
        (acc, [placeholder, original]) => acc.replace(placeholder, original),
        text
    );
}

// ─── Hook com debounce para usar em componentes ───────────────────────────────
// Uso: const lang = useDetectLanguage(text);

import { useState, useEffect } from 'react';

export const useDetectLanguage = (text: string, debounceMs = 600): string => {
    const [lang, setLang] = useState<string>(userLang);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLang(detectLanguage(text));
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [text, debounceMs]);

    return lang;
};